import { Badge } from '@workspace/ui/components/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@workspace/ui/components/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@workspace/ui/components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@workspace/ui/components/tooltip';
import { DollarSign, HelpCircle, Info } from 'lucide-react';
import { m } from '~/paraglide/messages.js';
import type {
  CardMetadata,
  FeeBreakdown,
  SpendingFee,
} from '../../-types/card';

interface FeeViewerProps {
  metadata: CardMetadata;
}

export function FeeViewer({ metadata }: FeeViewerProps) {
  const fees = metadata.spending_fees;
  const hasFees = fees && fees.length > 0;

  return (
    <Card className="border-purple-500/30 bg-transparent shadow-none">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <DollarSign className="h-4 w-4 text-purple-400" />
            {m['cards.fees.title']()}
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[300px]">
                <p className="text-xs">{m['cards.fees.tooltip']()}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent>
        {hasFees ? <FeeTable fees={fees} /> : <DefaultFeesInfo />}
      </CardContent>
    </Card>
  );
}

function DefaultFeesInfo() {
  return (
    <div className="flex items-start gap-3 rounded-md border border-dashed border-purple-500/20 p-4">
      <Info className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
      <div>
        <p className="text-sm font-medium">
          {m['cards.fees.using_defaults']()}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {m['cards.fees.defaults_description']()}
        </p>
      </div>
    </div>
  );
}

function FeeTable({ fees }: { fees: SpendingFee[] }) {
  return (
    <div className="rounded-md border border-purple-500/20 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">
              {m['cards.fees.fee_name']()}
            </TableHead>
            <TableHead className="text-xs">
              {m['cards.fees.fee_type']()}
            </TableHead>
            <TableHead className="text-xs">
              {m['cards.fees.fee_rate']()}
            </TableHead>
            <TableHead className="text-xs">
              {m['cards.fees.fee_rule']()}
            </TableHead>
            <TableHead className="text-xs">
              {m['cards.fees.fee_category']()}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fees.map((fee, i) => (
            <TableRow key={`${fee.fee_name}-${i}`}>
              <TableCell className="text-sm font-medium">
                {fee.fee_name}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs">
                  {fee.type === 'percentage'
                    ? m['cards.fees.type_percentage']()
                    : m['cards.fees.type_flat']()}
                </Badge>
              </TableCell>
              <TableCell className="text-sm font-mono">
                {formatFeeValue(fee)}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatFeeRule(fee)}
              </TableCell>
              <TableCell>
                {fee.category && (
                  <Badge variant="secondary" className="text-xs capitalize">
                    {fee.category}
                  </Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function formatFeeValue(fee: SpendingFee): string {
  if (fee.type === 'percentage') {
    return `${(fee.value * 100).toFixed(2)}%`;
  }
  return `$${fee.value.toFixed(2)}`;
}

function formatFeeRule(fee: SpendingFee): string {
  if (!fee.rule) return m['cards.fees.rule_always']();

  switch (fee.rule) {
    case 'fx_conversion':
      return m['cards.fees.rule_fx_conversion']();
    case 'amount_range_usd': {
      const params = fee.rule_params ?? {};
      const min = params.min ?? '0';
      const max = params.max ?? '∞';
      return `$${min} – $${max} USD`;
    }
    case 'wallet': {
      const wallet = (fee.rule_params?.wallet_name as string) ?? 'unknown';
      return `${wallet} wallet`;
    }
    default:
      return fee.rule;
  }
}

interface TransactionFeeBreakdownProps {
  feeBreakdown: FeeBreakdown;
}

export function TransactionFeeBreakdown({
  feeBreakdown,
}: TransactionFeeBreakdownProps) {
  if (!feeBreakdown?.fees?.length) return null;

  return (
    <div className="mt-3 space-y-2 border-t pt-3">
      <p className="text-xs font-medium">{m['cards.fees.txn_fees_title']()}</p>
      <div className="space-y-1">
        {feeBreakdown.fees.map((fee, i) => (
          <div
            key={`${fee.fee_name}-${i}`}
            className="flex items-center justify-between text-xs"
          >
            <span className="text-muted-foreground">
              {fee.fee_name} ({(fee.rate * 100).toFixed(2)}%)
            </span>
            <span className="font-mono">${fee.amount}</span>
          </div>
        ))}
        <div className="flex items-center justify-between text-xs font-medium pt-1 border-t">
          <span>{m['cards.fees.txn_fees_total']()}</span>
          <span className="font-mono">${feeBreakdown.total_fees}</span>
        </div>
      </div>
    </div>
  );
}
